import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Modal as SemanticModal } from "semantic-ui-react";
import ReactTooltip from "react-tooltip";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import { useDictionary } from "hooks";
import Modal from "@cayuse/shared-react.components.modal";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import RequiredIndicator from "components/RequiredIndicator";
import CustomDropdown from "@cayuse/shared-react.custom-dropdown";
import Button from "@cayuse/shared-react.components.button";
import TextArea from "@cayuse/shared-react.components.form.inputs.text-area";
import styles from "./styles.css";


const StatusCommentModal = ({
	comment,
	resolutions,
	resolutionSelected,
	resolutionsVisible,
	title,
	visible,
	onClose,
	onConfirm,
	onCommentChange,
	onResolutionChange,
	isLoading,
	formType
}) => {
	const defaults = {
		title: `Change ${formType} Status`,
		headerLabel: "Comment",
		cancelLabel: "Cancel",
		saveLabel: "Save",
		placeholder: "Please add a comment.",
		disabledSave: true,
		disabledReason: null,
		resolutionLabel: "Resolution",
		resolutionPlaceholder: "Select resolution"
	};
	const lowerCaseFormType = formType.toLowerCase();
	const capitalizedFormType = lowerCaseFormType.charAt(0).toUpperCase() + lowerCaseFormType.slice(1)

	const dictionary = useDictionary(`${lowerCaseFormType}s.summary`);

	const [ disabledReason, setDisabledReason ] = useState(defaults.disabledReason);
	const [ disabledSave, setDisabledSave ] = useState(defaults.disabledSave);
	const [ headerLabel, setHeaderLabel ] = useState(defaults.placeholder);

	const resolutionDropdownRef = useRef(null);

	const hasResolutions = isNonEmptyArray(resolutions);


	useEffect(() => {
		const commentRequired = !comment || !isNonEmptyString(comment.trim());

		if (hasResolutions && resolutionsVisible) {
			setDisabledSave(!resolutionSelected || commentRequired);
		} else {
			setDisabledSave(commentRequired);
		}
	}, [ comment, hasResolutions, resolutionSelected, resolutionsVisible ]);

	useEffect(() => {
		if ( disabledSave) {
			if (hasResolutions && resolutionsVisible) {
				setDisabledReason(dictionary.selectResolutionLabel || defaults.placeholder);
			} else {
				setDisabledReason(dictionary.editCommentPlaceholder || defaults.placeholder);
			}
		} else {
			setDisabledReason(null);
		}
	}, [
		disabledSave,
		hasResolutions,
		resolutionsVisible,
		defaults.placeholder,
		dictionary.editCommentPlaceholder,
		dictionary.selectResolutionLabel
	]);

	useEffect(() => {
		if (resolutionsVisible) {
			setHeaderLabel(dictionary.selectResolutionLabel || defaults.placeholder);
		} else {
			setHeaderLabel(dictionary.editCommentPlaceholder || defaults.placeholder);
		}
	}, [ resolutionsVisible, defaults.placeholder, dictionary.editCommentPlaceholder, dictionary.selectResolutionLabel ]);

	useEffect(() => {
		if (visible && resolutionsVisible && hasResolutions && resolutionDropdownRef?.current && resolutionDropdownRef?.current?.querySelector("[tabindex='0']")) {
			setTimeout(() => {
				resolutionDropdownRef.current.querySelector("button").focus();
			}, 0);
		}
	}, [ resolutionsVisible, hasResolutions, resolutionDropdownRef, visible ]);

	useEffect(() => {
		if (visible) {
			setDisabledSave(defaults.disabledSave);
			ReactTooltip.rebuild();
		}
	}, [ defaults.disabledSave, visible ]);

	return (
		<Modal
			closeIcon
			open={visible}
			onClose={onClose}
			data-component={`${capitalizedFormType}StatusCommentModal`}
			ariaLabel={title || defaults.title}
			closeOnEscape={false}
		>
			<SemanticModal.Header className="ui header whiteOnBlue">
				<h2>{title || defaults.title}</h2>
			</SemanticModal.Header>
			<SemanticModal.Content className={styles.content}>
				<VerticalSpacing>
					<div className={"ui input"}>
						<b>{headerLabel}</b>
					</div>
				</VerticalSpacing>
				{resolutionsVisible && hasResolutions &&
				<>
					<VerticalSpacing>
						<div className={"ui input"}>
							<label>{dictionary.resolutionLabel || defaults.resolutionLabel}</label>
							<RequiredIndicator/>
						</div>
						<div ref={resolutionDropdownRef} role="application" className={`ui input ${styles.dropdownWrapper}`}>
							<CustomDropdown
								ariaLabel={dictionary.resolutionPlaceholder || defaults.resolutionPlaceholder}
								value={resolutionSelected || resolutions[0].value}
								onChange={(event, { value }) => onResolutionChange(value)}
								customClassname={styles.resolutionDropdown}
								options={resolutions}
								isCustomized={true}
								isCustomStyling={true}
								isCustomOnChange={true}
							/>
						</div>
					</VerticalSpacing>
				</>
				}
				<div className={"ui input"}>
					<label>{dictionary.editCommentLabel || defaults.headerLabel}</label>
					<RequiredIndicator/>
				</div>
				<TextArea
					placeholder={dictionary.editCommentPlaceholder || defaults.placeholder}
					value={comment}
					onChange={event => onCommentChange(event.target.value)}
				/>
			</SemanticModal.Content>
			<VerticalSpacing/>
			<SemanticModal.Actions>

				<div className={`field ${styles.statusCommentModal__actions}`}>
					<Button secondary onClick={onClose}>{dictionary.cancelCommentLabel || defaults.cancelLabel}</Button>
					<Button
						onClick={onConfirm}
						disabled={disabledSave || isLoading}
						busy={isLoading}
						loadingMessage={`Changing ${lowerCaseFormType} status`}
						tooltip={disabledSave && disabledReason}
					>
						{dictionary.saveCommentLabel || defaults.saveLabel}
					</Button>
				</div>
			</SemanticModal.Actions>
		</Modal>
	);
};

StatusCommentModal.propTypes = {
	comment: PropTypes.string,
	resolutions: PropTypes.array,
	/*resolutionSelected,
	resolutionsVisible,
	title,
	visible,
	onClose,
	onConfirm,
	onCommentChange,
	onResolutionChange*/
	formType: PropTypes.string
};

export default StatusCommentModal;